import { cn } from '@/utils';
import { useEffect, useState } from 'react';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface StyleFilterStrengthProps {
  className?: string;
  initValue?: number;
  onChange: (weight: number) => void;
}

const SfStrengthItem = ({ value, text, isActive }: { value: number; text: string; isActive: boolean }) => {
  return (
    <TabsTrigger
      value={String(value)}
      className="!mr-[1px] relative text-sm w-[36px] h-[36px] font-medium rounded-sm text-[#5a5b5c] bg-[#1a1c1d] transition duration-200 data-[state=active]:border-[0.5px] data-[state=active]:border-[#474A4C] data-[state=active]:bg-[linear-gradient(138deg,_#FF5C28_4.64%,_#C24118_95.71%)] data-[state=active]:text-[#e9ecec] text-[14px]"
    >
      {isActive ? <ActiveIcon className=" absolute left-[3px] top-[3px]" /> : <InactiveIcon className="absolute left-[4px] top-[4px]" />}
      {text}
    </TabsTrigger>
  );
};

const StyleFilterStrength = ({ className, initValue = 0, onChange }: StyleFilterStrengthProps) => {
  const [weight, setWeight] = useState(initValue);
  useEffect(() => {
    onChange(weight);
  }, [weight]);

  return (
    <Tabs defaultValue={String(weight)} onValueChange={(val) => setWeight(Number(val))} className={cn('w-full', className)}>
      <TabsList className="w-full bg-[#1a1c1d] text-jen-size-heading-02 mt-jen-spacing-12 p-[2px] rounded">
        <SfStrengthItem value={0} text="01" isActive={weight === 0} />
        <SfStrengthItem value={0.25} text="02" isActive={weight === 0.25} />
        <SfStrengthItem value={0.5} text="03" isActive={weight === 0.5} />
        <SfStrengthItem value={0.75} text="04" isActive={weight === 0.75} />
        <SfStrengthItem value={1} text="05" isActive={weight === 1} />
      </TabsList>
      <div className="w-[184px] h-[5px] ml-[2px] mt-[5px]">
        <svg width="184" height="6" viewBox="0 0 184 6" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1 0V5H35.8V0" stroke="white" stroke-width="0.5" />
          <path d="M37.8 0V5H72.6V0" stroke="white" stroke-width="0.5" />
          <path d="M74.6001 0V5H109.4V0" stroke="white" stroke-width="0.5" />
          <path d="M111.4 0V5H146.2V0" stroke="white" stroke-width="0.5" />
          <path d="M148.2 0V5H183V0" stroke="white" stroke-width="0.5" />
        </svg>
      </div>
      <div className="mt-[9px] text-center w-full text-[#e9ecec] font-pressura-mono text-[10px]">STRENGTH</div>
    </Tabs>
  );
};

const InactiveIcon = ({ className }: { className?: string }) => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="5" height="4" viewBox="0 0 5 4" fill="none" className={className}>
      <path
        d="M4.02342 2.08423C4.02342 1.24287 3.34136 0.560805 2.5 0.560805C1.65864 0.560804 0.976576 1.24286 0.976576 2.08423C0.976576 2.92559 1.65864 3.60765 2.5 3.60765C3.34136 3.60765 4.02342 2.92559 4.02342 2.08423Z"
        fill="url(#paint0_linear_3159_966)"
        stroke="url(#paint1_linear_3159_966)"
        stroke-width="0.0468488"
      />
      <ellipse cx="2.5" cy="2.08447" rx="1.375" ry="1.375" fill="url(#paint2_linear_3159_966)" />
      <defs>
        <linearGradient id="paint0_linear_3159_966" x1="1.13125" y1="3.35387" x2="3.50915" y2="0.910891" gradientUnits="userSpaceOnUse">
          <stop stop-color="#E0E0E0" />
          <stop offset="1" stop-color="#817F7F" />
        </linearGradient>
        <linearGradient id="paint1_linear_3159_966" x1="1.25" y1="2.95923" x2="3.08311" y2="0.372072" gradientUnits="userSpaceOnUse">
          <stop stop-color="#F6F6F6" />
          <stop offset="1" stop-color="#CECECE" stop-opacity="0.15" />
        </linearGradient>
        <linearGradient id="paint2_linear_3159_966" x1="1.33616" y1="0.829785" x2="3.57556" y2="3.00952" gradientUnits="userSpaceOnUse">
          <stop offset="0.01" stop-color="#838383" />
          <stop offset="0.995" stop-color="#CAC7C7" />
        </linearGradient>
      </defs>
    </svg>
  );
};

const ActiveIcon = ({ className }: { className?: string }) => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="9" height="8" viewBox="0 0 9 8" fill="none" className={className}>
      <circle
        cx="3.21878"
        cy="3.08408"
        r="2.81901"
        transform="rotate(-90 3.21878 3.08408)"
        fill="url(#paint0_linear_3159_974)"
        stroke="url(#paint1_linear_3159_974)"
        stroke-width="0.0866911"
      />
      <circle cx="3.21843" cy="3.08416" r="2.54436" fill="url(#paint2_linear_3159_974)" />
      <g opacity="0.8">
        <g>
          <ellipse cx="3.2188" cy="2.65233" rx="2.59062" ry="2.28222" fill="url(#paint3_radial_3159_974)" />
        </g>
      </g>
      <g>
        <ellipse
          cx="2.71889"
          cy="2.9132"
          rx="2.71889"
          ry="2.9132"
          transform="matrix(0.92755 0.373699 -0.512527 0.858671 2.99707 0.44873)"
          fill="url(#paint4_radial_3159_974)"
        />
      </g>
      <g>
        <ellipse
          cx="2.15211"
          cy="2.9132"
          rx="2.15211"
          ry="2.9132"
          transform="matrix(0.92755 0.373699 -0.512527 0.858671 3.51758 0)"
          fill="url(#paint5_radial_3159_974)"
        />
      </g>
      <defs>
        <linearGradient id="paint0_linear_3159_974" x1="0.869379" y1="0.551279" x2="5.38998" y2="4.95144" gradientUnits="userSpaceOnUse">
          <stop stop-color="#FE5B27" />
          <stop offset="1" stop-color="#C64219" />
        </linearGradient>
        <linearGradient id="paint1_linear_3159_974" x1="1.59964" y1="0.77102" x2="6.38703" y2="4.16308" gradientUnits="userSpaceOnUse">
          <stop stop-color="#F98A67" />
          <stop offset="1" stop-color="#F98A67" stop-opacity="0.15" />
        </linearGradient>
        <linearGradient id="paint2_linear_3159_974" x1="1.06481" y1="0.762426" x2="5.2087" y2="4.79591" gradientUnits="userSpaceOnUse">
          <stop offset="0.01" stop-color="#C64219" />
          <stop offset="0.995" stop-color="#FE5B27" />
        </linearGradient>
        <radialGradient
          id="paint3_radial_3159_974"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(3.28048 3.45951) rotate(92.3945) scale(1.47632 1.67826)"
        >
          <stop stop-color="#F76431" />
          <stop offset="1" stop-color="#F76431" stop-opacity="0" />
        </radialGradient>
        <radialGradient
          id="paint4_radial_3159_974"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(2.71889 2.9132) rotate(90) scale(2.9132 2.71889)"
        >
          <stop stop-color="#FF5B27" />
          <stop offset="1" stop-color="#FF5B27" stop-opacity="0" />
        </radialGradient>
        <radialGradient
          id="paint5_radial_3159_974"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(2.15211 2.9132) rotate(90) scale(2.9132 2.15211)"
        >
          <stop stop-color="#F5A65D" />
          <stop offset="1" stop-color="#F5A65D" stop-opacity="0" />
        </radialGradient>
      </defs>
    </svg>
  );
};

export default StyleFilterStrength;
